<script setup>
import {getCurrentInstance, ref} from "vue";

const props = defineProps({
  /**
   * Optional identifier of the chart container this button belongs to. It is
   * passed down from pages such as `KpiAnalysis.vue` or `PsiAnalysis.vue`
   * (e.g. `psi1`, `bar5`). When provided, the button first tries to locate the
   * chart canvas inside the element with this id. When it is missing or not
   * unique, the button falls back to locating the closest chart canvas
   * relative to its own position in the DOM, so downloading always works.
   */
  id: {
    type: String,
    required: false,
    default: null,
  },

  /**
   * Image format for the download. Only `png` and `jpg`/`jpeg` are supported.
   */
  format: {
    type: String,
    required: false,
    default: "jpg",
    validator: (value) => ["png", "jpg", "jpeg"].includes(value.toLowerCase()),
  },

  /**
   * Optional custom file name (without extension) for the downloaded image.
   */
  fileName: {
    type: String,
    required: false,
    default: null,
  },
});

// Reference to this component's root element, used as a fallback anchor to
// find the related chart canvas when an id is not available/unique.
const root = ref(null);
const instance = getCurrentInstance();

/**
 * Resolves the chart `<canvas>` element related to this button.
 *
 * Strategy:
 *  1. If an `id` is given and the element exists, look for a canvas inside it.
 *  2. Otherwise walk up the DOM from the button and grab the closest canvas.
 */
function resolveCanvas() {
  if (props.id) {
    const container = document.getElementById(props.id);
    const canvas = container?.querySelector("canvas");
    if (canvas) return canvas;
  }

  let node = root.value ?? instance?.vnode?.el ?? null;
  while (node && node.parentElement) {
    node = node.parentElement;
    const canvas = node.querySelector("canvas");
    if (canvas) return canvas;
  }

  return null;
}

/**
 * Renders the given canvas onto a fresh canvas. For JPEG we first paint a white
 * background, since a transparent canvas would otherwise become black.
 */
function toImageDataUrl(canvas, mimeType) {
  const output = document.createElement("canvas");
  output.width = canvas.width;
  output.height = canvas.height;

  const context = output.getContext("2d");

  if (mimeType === "image/jpeg") {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, output.width, output.height);
  }

  context.drawImage(canvas, 0, 0);
  return output.toDataURL(mimeType, 1.0);
}

function downloadChart() {
  const canvas = resolveCanvas();

  if (!canvas) {
    console.error("DownloadChartButton: could not find a chart canvas to download.");
    return;
  }

  const normalized = props.format.toLowerCase();
  const isJpg = normalized === "jpg" || normalized === "jpeg";
  const mimeType = isJpg ? "image/jpeg" : "image/png";
  const extension = isJpg ? "jpg" : "png";

  const dataUrl = toImageDataUrl(canvas, mimeType);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `${props.fileName ?? props.id ?? "chart"}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
</script>

<template>
  <v-tooltip contained location="left" text="Download the chart">
    <template v-slot:activator="{ props }">
      <v-btn
          ref="root"
          v-bind="props"
          @click="downloadChart"
          rounded
          flat
          size="small"
          color="blue-darken-4"
          icon="fa fa-download"
      />
    </template>
  </v-tooltip>
</template>
